import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../utils/api/axiosInstance";
import { resolveComparisonAiSession } from "../../utils/resolveComparisonAiSession";
import { getMatchPercent, formatFitScoreBadge } from "../../utils/matchScoreUtils";
import {
  buildFallbackRecommendation,
  buildQuickComparisonTable,
  getComparisonPicks,
  getDefaultCategories,
} from "../../utils/quickComparisonUtils";
import { buildComparisonSummaryFallback } from "../../utils/comparisonSummaryUtils";
import {
  buildComparisonSummaryCacheKey,
  fetchShortlistAiCached,
  readShortlistAiCache,
} from "../../utils/shortlistAiCache";
import styles from "./ComparisonAiSummary.module.css";

const enrichWithSessionMatchData = (products = [], aiSession = null) => {
  const sessionById = new Map(
    (aiSession?.products || []).map((product) => [
      String(product._id || product.id),
      product,
    ]),
  );

  return products.map((product) => {
    const sessionProduct = sessionById.get(String(product._id || product.id));
    if (!sessionProduct) return product;

    return {
      ...product,
      matchScore:
        product.matchScore ?? sessionProduct.matchScore ?? sessionProduct.score,
      rank: product.rank ?? sessionProduct.rank,
      matchExplanation:
        product.matchExplanation ?? sessionProduct.matchExplanation,
    };
  });
};

const getProductPath = (product) => {
  const catId = product?.category?._id || product?.category;
  if (catId) return `/category/${catId}/product/${product._id}`;
  return `/product/${product._id}`;
};

const normalizeTradeoffItems = (items = []) =>
  items
    .flatMap((item) => String(item || "").split(/;\s*/))
    .map((item) => item.trim())
    .filter(Boolean);

const formatProductPrice = (product) => {
  const n = Number(product?.price);
  return Number.isFinite(n) ? `$${n.toLocaleString()}` : null;
};

const splitSummaryParagraphs = (text) =>
  String(text || "")
    .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
    .map((part) => part.trim())
    .filter(Boolean);

const TradeoffList = ({ label, labelClassName, itemClassName, items }) => {
  const normalized = normalizeTradeoffItems(items);
  if (!normalized.length) return null;

  return (
    <div className={styles.tradeoffGroup}>
      <p className={labelClassName}>{label}</p>
      <ul className={styles.tradeoffList}>
        {normalized.map((item, index) => (
          <li
            key={`${label}-${index}-${item}`}
            className={`${styles.tradeoffListItem} ${itemClassName}`}
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ComparisonAiSummary = ({
  products = [],
  aiSession = null,
  searchParams = null,
}) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const productsWithMatchData = useMemo(
    () => enrichWithSessionMatchData(products, aiSession),
    [products, aiSession],
  );
  const effectiveSession = useMemo(
    () =>
      resolveComparisonAiSession({
        products: productsWithMatchData,
        aiSession,
        searchParams,
      }),
    [productsWithMatchData, aiSession, searchParams],
  );
  const effectiveSearchParams =
    searchParams || effectiveSession?.searchParams || null;
  const comparisonTable = useMemo(
    () =>
      buildQuickComparisonTable({
        products: productsWithMatchData,
        categories: getDefaultCategories(effectiveSearchParams),
        searchParams: effectiveSearchParams,
      }),
    [productsWithMatchData, effectiveSearchParams],
  );
  const comparisonPicks = useMemo(
    () => getComparisonPicks(productsWithMatchData, effectiveSearchParams),
    [productsWithMatchData, effectiveSearchParams],
  );
  const topMatch =
    comparisonPicks.topMatchIndex >= 0
      ? productsWithMatchData[comparisonPicks.topMatchIndex]
      : null;
  const valuePick =
    comparisonPicks.valuePickIndex >= 0
      ? productsWithMatchData[comparisonPicks.valuePickIndex]
      : null;
  const valueRecommendation = useMemo(
    () =>
      buildFallbackRecommendation(
        productsWithMatchData,
        effectiveSearchParams,
        comparisonPicks.valuePickIndex,
      ),
    [
      productsWithMatchData,
      effectiveSearchParams,
      comparisonPicks.valuePickIndex,
    ],
  );
  const localFallback = useMemo(() => {
    const topId = topMatch ? String(topMatch._id || topMatch.id) : null;
    const valueId = valuePick ? String(valuePick._id || valuePick.id) : null;
    return buildComparisonSummaryFallback({
      products: productsWithMatchData,
      searchParams: effectiveSearchParams,
      comparisonTable,
      topMatchProductId: topId,
      valuePickProductId: valueId,
    });
  }, [
    productsWithMatchData,
    effectiveSearchParams,
    comparisonTable,
    topMatch,
    valuePick,
  ]);

  const productKey = useMemo(
    () => productsWithMatchData.map((p) => p._id || p.id).join(","),
    [productsWithMatchData],
  );
  const sessionKey = useMemo(
    () =>
      JSON.stringify({
        searchParams: effectiveSession?.searchParams || null,
        matchProfile:
          effectiveSession?.matchProfile?.summary ||
          effectiveSession?.matchProfile?.primaryUseCase ||
          null,
      }),
    [effectiveSession],
  );
  const tableKey = useMemo(
    () =>
      JSON.stringify(
        (comparisonTable?.rows || []).map((r) => ({
          k: r.key,
          b: r.bestIndex,
          v: r.values,
        })),
      ),
    [comparisonTable],
  );
  const topMatchId = topMatch ? String(topMatch._id || topMatch.id) : null;
  const valuePickId = valuePick ? String(valuePick._id || valuePick.id) : null;

  const requestKey = useMemo(
    () =>
      buildComparisonSummaryCacheKey({
        productKey,
        sessionKey,
        tableKey,
        topMatchId,
        valuePickId,
      }),
    [productKey, sessionKey, tableKey, topMatchId, valuePickId],
  );

  const latestRef = useRef();
  latestRef.current = {
    productsWithMatchData,
    effectiveSession,
    comparisonTable,
    topMatch,
    valuePick,
    localFallback,
  };
  const completedFetchKeyRef = useRef(null);

  useEffect(() => {
    const {
      productsWithMatchData: currentProducts,
      effectiveSession: currentSession,
      comparisonTable: currentTable,
      topMatch: currentTopMatch,
      valuePick: currentValuePick,
      localFallback: currentFallback,
    } = latestRef.current;

    if (!Array.isArray(currentProducts) || currentProducts.length < 2) {
      setSummary(null);
      setLoading(false);
      completedFetchKeyRef.current = null;
      return;
    }

    if (completedFetchKeyRef.current === requestKey) {
      return;
    }

    const cached = readShortlistAiCache("comparisonSummary", requestKey);
    if (cached !== undefined) {
      completedFetchKeyRef.current = requestKey;
      setSummary(cached || currentFallback);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchShortlistAiCached({
      namespace: "comparisonSummary",
      key: requestKey,
      fetcher: async () => {
        const res = await api.post("/ai/grok/comparison-summary", {
          products: currentProducts.map((p) => ({
            _id: p._id,
            name: p.name,
            brand: p.brand,
            price: p.price,
            category: p.category,
            matchScore: p.matchScore ?? p.score ?? null,
            rank: p.rank ?? null,
            specifications: p.specifications || [],
          })),
          aiSession: currentSession || undefined,
          matchProfile: currentSession?.matchProfile || undefined,
          comparisonTable: currentTable || undefined,
          topMatchProductId: currentTopMatch
            ? String(currentTopMatch._id || currentTopMatch.id)
            : undefined,
          valuePickProductId: currentValuePick
            ? String(currentValuePick._id || currentValuePick.id)
            : undefined,
        });
        return res.data?.summary || null;
      },
    })
      .then(({ data }) => {
        if (cancelled) return;
        completedFetchKeyRef.current = requestKey;
        setSummary(data || currentFallback);
      })
      .catch(() => {
        if (cancelled) return;
        completedFetchKeyRef.current = requestKey;
        setSummary(currentFallback);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestKey]);

  const displaySummary = summary || localFallback;

  if (loading) {
    return (
      <div className={styles.card}>
        <p className={styles.loading}>Generating comparison summary…</p>
      </div>
    );
  }

  const hasNarrative = Boolean(displaySummary?.summary);
  const hasPicks = Boolean(topMatch);
  if (!hasNarrative && !hasPicks) return null;

  const topMatchScore = topMatch ? getMatchPercent(topMatch) : 0;
  const valuePickScore = valuePick ? getMatchPercent(valuePick) : 0;
  const valuePickPrice = valuePick ? formatProductPrice(valuePick) : null;
  const summaryParagraphs = hasNarrative
    ? splitSummaryParagraphs(displaySummary.summary)
    : [];
  const showValuePick =
    comparisonPicks.isValuePick && Boolean(valuePick);
  const valuePickReason =
    !hasNarrative && valueRecommendation?.summary
      ? valueRecommendation.summary
      : null;

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <h2 className={styles.title}>Comparison summary</h2>
        <p className={styles.intro}>
          {showValuePick
            ? `Top match by score and best value pick can differ — both are shown below.`
            : `These products are ranked by how closely they match your search.`}
        </p>
      </header>

      {(topMatch || showValuePick) && (
        <div className={styles.picksGrid}>
          {topMatch && (
            <article className={`${styles.pickCard} ${styles.pickCardTop}`}>
              <p className={styles.pickLabel}>Top match by score</p>
              <div className={styles.pickBody}>
                <p className={styles.pickName}>{topMatch.name}</p>
                {topMatchScore > 0 && (
                  <p className={styles.pickMeta}>{formatFitScoreBadge(topMatchScore)}</p>
                )}
              </div>
              <Link to={getProductPath(topMatch)} className={styles.pickLink}>
                View details
              </Link>
            </article>
          )}

          {showValuePick && (
            <article className={`${styles.pickCard} ${styles.pickCardValue}`}>
              <p className={styles.pickLabel}>Best value pick</p>
              <div className={styles.pickBody}>
                <p className={styles.pickName}>{valuePick.name}</p>
                <p className={styles.pickMeta}>
                  {[
                    valuePickPrice,
                    valuePickScore > 0 ? formatFitScoreBadge(valuePickScore) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {valuePickReason && (
                  <p className={styles.pickReason}>{valuePickReason}</p>
                )}
              </div>
              <Link to={getProductPath(valuePick)} className={styles.pickLink}>
                View details
              </Link>
            </article>
          )}
        </div>
      )}

      {summaryParagraphs.length > 0 && (
        <section className={styles.section} aria-label="Comparison overview">
          <h3 className={styles.sectionTitle}>Overview</h3>
          {summaryParagraphs.map((paragraph, index) => (
            <p key={`overview-${index}`} className={styles.summary}>
              {paragraph}
            </p>
          ))}
        </section>
      )}

      {displaySummary?.tradeoffs?.length > 0 && (
        <section className={styles.section} aria-label="Product trade-offs">
          <h3 className={styles.sectionTitle}>Strengths & trade-offs</h3>
          <div className={styles.tradeoffsGrid}>
            {productsWithMatchData.map((prod) => {
              const t = displaySummary.tradeoffs.find(
                (entry) => String(entry.productId) === String(prod._id),
              );
              if (!t) return null;

              const isTopMatchProduct =
                topMatch && String(topMatch._id) === String(prod._id);
              const isValuePickProduct =
                showValuePick &&
                String(valuePick._id) === String(prod._id);
              const hasPros = normalizeTradeoffItems(t.pros).length > 0;
              const hasCons = normalizeTradeoffItems(t.cons).length > 0;
              if (!hasPros && !hasCons) return null;

              return (
                <article key={prod._id} className={styles.tradeoffCard}>
                  <header className={styles.tradeoffHeader}>
                    <h4 className={styles.tradeoffName}>{prod.name}</h4>
                    {(isTopMatchProduct || isValuePickProduct) && (
                      <div className={styles.tradeoffBadges}>
                        {isTopMatchProduct && (
                          <span className={styles.tradeoffBadgeTop}>Top match</span>
                        )}
                        {isValuePickProduct && (
                          <span className={styles.tradeoffBadgeValue}>
                            Best value
                          </span>
                        )}
                      </div>
                    )}
                  </header>

                  <TradeoffList
                    label="Strengths"
                    labelClassName={styles.proLabel}
                    itemClassName={styles.tradeoffListItemPro}
                    items={t.pros}
                  />
                  <TradeoffList
                    label="Trade-offs"
                    labelClassName={styles.conLabel}
                    itemClassName={styles.tradeoffListItemCon}
                    items={t.cons}
                  />
                </article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default ComparisonAiSummary;
